<template>
  <v-row
    class="ma-0"
    style="max-width: 100%"
  >
    <v-col
      class="pa-0"
      style="max-width: 100%"
    >
      <v-card
        border
        class="pt-6"
        style="max-width: 100%"
      >
        <!-- TODO check missing method :row-props="getRowProps" -->
        <v-data-table-server
          ref="table"
          v-model:selected="selectedValue"
          :headers="headers"
          :items="leads"
          :loading="loading"
          :loading-text="$t('leads.table.loading-text')"
          :no-data-text="noDataText"
          :items-per-page="internalItemsPerPage"
          :items-per-page-options="itemsPerPageOptions"
          :items-length="serverItemsLength"
          :show-select="showSelect"
          v-model:options="internalOptions"
          :height="listHeight"
          fixed-header
          hover
          :class="{ 'td-small': $vuetify.display.smAndDown }"
          :row-props="getRowClass"
          @click:row="handleRowClick"
          @change="selectAllVisibleItems"
        >
          <!-- Headers -->
          <template #[`header.assignedUser`]>
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <span v-bind="props">{{ $t('leads.table.labels.employee') }}</span>
              </template>
              <span>{{ $t('leads.table.actions.assigned-user') }}</span>
            </v-tooltip>
          </template>

          <template #[`header.city`]>
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <span v-bind="props">{{ $t('leads.table.labels.city') }}</span>
              </template>
              <span>{{ $t('leads.table.actions.city') }}</span>
            </v-tooltip>
          </template>

          <template #[`header.zip`]>
            <v-tooltip location="top">
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ $t('leads.table.labels.zip') }}</span>
              </template>
              <span>{{ $t('leads.table.actions.zip') }}</span>
            </v-tooltip>
          </template>

          <template #[`header.data-table-select`]="{ props }">
            <v-checkbox-btn
              color="primary"
              v-bind="props"
              :ripple="false"
            />
          </template>

          <!-- Items -->
          <template #[`item.data-table-select`]="{ item }">
            <v-checkbox-btn
              v-if="!item.invitationBlockedReason"
              color="primary"
              :model-value="selectedValue.some(({ id }) => id === item.id)"
              :ripple="false"
              @update:model-value="(v) => handleItemSelection(item, v)"
              @click.stop
            />
            <v-tooltip
              v-else
              location="top"
            >
              <template v-slot:activator="{ props }">
                <v-icon
                  class="deactivated-checkbox"
                  v-bind="props"
                >
                  mdi-alert-circle-check-outline
                </v-icon>
              </template>
              <span>{{ $t(readableInvitationBlockedReason(item)) }}</span>
            </v-tooltip>
          </template>

          <template #[`item.exportedAt`]="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <div
                  style="min-width: 80px"
                  v-bind="props"
                >
                  <v-icon v-if="item.exportedAt"> mdi-check </v-icon>
                </div>
              </template>
              <span v-if="item.exportedAt">{{ formatDate(item.exportedAt) }}</span>
            </v-tooltip>
          </template>

          <template #[`item.createdAt`]="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <div
                  style="min-width: 60px"
                  v-bind="props"
                >
                  <span>{{ formatDate(item.createdAt, 'dateShort') }}</span>
                </div>
              </template>
              <span>{{ formatDate(item.createdAt) }}</span>
            </v-tooltip>
          </template>

          <template #[`item.lastConversionAt`]="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <div
                  style="min-width: 100px"
                  v-bind="props"
                >
                  <span>{{ formatDate(item.lastConversionAt, 'dateShort') }}</span>
                </div>
              </template>
              <span>{{ formatDate(item.lastConversionAt) }}</span>
            </v-tooltip>
          </template>

          <template #[`item.dueDate`]="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <div
                  style="min-width: 100px"
                  v-bind="props"
                >
                  <span :class="{ 'due-date-red': isDayOrLessBeforeDueDate(item.dueDate) }">
                    {{ formatDate(item.dueDate, 'dateShort') }}
                  </span>
                </div>
              </template>
              <span>{{ formatDate(item.dueDate, 'dateShort') }}</span>
            </v-tooltip>
          </template>

          <template #[`item.propstackCrmTags`]="{ item }">
            <CrmTags :tags="crmTags(item.tags)" />
          </template>

          <template #[`item.flowfactCrmTags`]="{ item }">
            <CrmTags :tags="crmTags(item.tags)" />
          </template>

          <template #[`item.onofficeCrmTags`]="{ item }">
            <CrmTags :tags="crmTags(item.tags)" />
          </template>

          <template #[`item.propertyValuation`]="{ item }">
            <div v-if="hasPropertyValue(item)">
              <div class="mr-2 pt-0">
                <v-icon class="pa-1 property-value-icons"> mdi mdi-home-analytics </v-icon>
                <span>{{ propertyValue(item, formatPrice) }}</span>
              </div>
            </div>
          </template>

          <template #[`item.commission`]="{ item }">
            <div v-if="commissionValue(item)">
              <div>
                <v-icon class="pa-1 property-value-icons"> mdi mdi-percent </v-icon>
                <span>{{ commissionValue(item, commission, formatPrice) }}</span>
              </div>
            </div>
          </template>

          <template #[`item.name`]="{ item }">
            <div :style="{ display: $vuetify.display.smAndDown ? 'inline-flex' : '' }">
              <router-link
                :to="{ name: 'lead-details', params: { id: item.id } }"
                class="d-flex h-full align-center"
                @click="handleNameClick($event)"
              >
                <template v-if="hasName(item)">
                  {{ formatName(item) }}
                </template>
                <template v-else>
                  {{ item.email }}
                </template>
              </router-link>
            </div>
          </template>

          <template #[`item.conversions`]="{ item }">
            <v-row no-gutters>
              <v-col
                cols="12"
                lg="12"
                md="8"
                sm="12"
                class="d-flex flex-column flex-sm-row flex-wrap align-start"
              >
                <div
                  v-for="{ type, label, color } of conversionTypes"
                  :key="type"
                >
                  <v-tooltip location="top">
                    <template v-slot:activator="{ on }">
                      <v-chip
                        v-if="showAction(item, type)"
                        :color="isPropertyRequestUrgent(item, type) ? 'red-lighten-4' : color"
                        size="x-small"
                        variant="flat"
                        class="ml-1"
                        rounded
                        v-on="isPropertyRequestUrgent(item, type) ? on : {}"
                      >
                        <span class="text-black">
                          {{ $t(label) }}
                        </span>
                      </v-chip>
                    </template>
                    <span>{{
                      isPropertyRequestUrgent(item, type)
                        ? $t('leads.table.tooltips.start-asap')
                        : ''
                    }}</span>
                  </v-tooltip>
                </div>

                <div v-if="isColumnVisible('conversions.download.postalShipping')">
                  <v-chip
                    v-if="showPostalShippingAction(item)"
                    color="amber-lighten-4"
                    variant="flat"
                    rounded
                    size="x-small"
                    class="ml-1"
                  >
                    <span class="text-black">{{ $t('leads.table.actions.postal-shipping') }}</span>
                  </v-chip>
                </div>

                <div v-if="isColumnVisible('conversionCountByType.DOWNLOAD')">
                  <v-chip
                    v-for="(amount, type) in getDownloadConversions(item)"
                    :key="type"
                    variant="flat"
                    rounded
                    :color="DOWNLOAD_TYPES[type].color"
                    size="x-small"
                    class="ml-1"
                  >
                    <span class="text-black">{{ $t(DOWNLOAD_TYPES[type].label) }}</span>
                  </v-chip>
                </div>

                <div v-if="isColumnVisible('conversions.preConversion.type.QUIZ')">
                  <v-chip
                    v-if="showQuizPreConversionAction(item)"
                    color="lime-lighten-4"
                    variant="flat"
                    rounded
                    size="x-small"
                    class="ml-1"
                  >
                    <span class="text-black">{{ $t('leads.table.actions.quiz-finished') }}</span>
                  </v-chip>
                </div>
              </v-col>
              <v-spacer />
            </v-row>
          </template>

          <template #[`item.currentStatus`]="{ item }">
            {{
              leadStatus[item.currentStatus]
                ? $t(leadStatus[item.currentStatus])
                : item.currentStatus
            }}
          </template>

          <template #[`item.assignedUser`]="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <div
                  style="min-width: 80px"
                  v-bind="props"
                >
                  <v-avatar
                    v-if="item.assignedUser"
                    color="grey"
                    size="30"
                  >
                    <span class="text-white text-caption font-weight-bold">
                      {{ getInitials(item.assignedUser) }}
                    </span>
                  </v-avatar>
                </div>
              </template>
              <span v-if="item.assignedUser">{{ formatName(item.assignedUser) }}</span>
            </v-tooltip>
          </template>

          <template #[`item.confirmedAt`]="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-icon
                  v-if="item.confirmedAt"
                  color="success"
                  v-bind="props"
                >
                  mdi-check-circle
                </v-icon>
                <v-icon
                  v-else
                  color="orange-lighten-3"
                  v-bind="props"
                >
                  mdi-clock
                </v-icon>
              </template>
              <span v-if="item.confirmedAt">{{ $t('leads.table.labels.confirmed') }}</span>
              <span v-else>{{ $t('leads.table.labels.not-confirmed') }}</span>
            </v-tooltip>
          </template>

          <template #[`item.email`]="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <span v-bind="props">
                  <span
                    class="d-inline-block text-truncate"
                    style="max-width: 150px"
                    >{{ item.email }}</span
                  >
                </span>
              </template>
              {{ item.email }}
            </v-tooltip>
          </template>

          <template #[`item.city`]="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <div
                  class="text-truncate"
                  style="max-width: 120px"
                  v-bind="props"
                >
                  {{ formatCity(item) }}
                </div>
              </template>
              <span>{{ formatCity(item) }}</span>
            </v-tooltip>
          </template>

          <template #[`item.zip`]="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <div
                  class="text-truncate"
                  style="min-width: 50px; max-width: 120px"
                  v-bind="props"
                >
                  {{ formatZip(item) }}
                </div>
              </template>
              <span>{{ formatZip(item) }}</span>
            </v-tooltip>
          </template>

          <!--          <template v-slot:footer.page-text="{ item }">
            {{ items.pageStart }} - {{ items.pageStop }} {{ $t('leads.table.of') }} {{ items.itemsLength }}
          </template>-->

          <template #[`bottom.page-text`]>
            {{ items.pageStart }} - {{ items.pageStop }} {{ $t('leads.table.of') }}
            {{ items.itemsLength }}
          </template>
        </v-data-table-server>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import status from '@/lib/leadStatus'
import brandingMixin from '@/mixins/branding'
import crmMixin from '@/mixins/crm'
import currencyMixin from '@/mixins/currency'
import leadsDueDateMixin from '@/modules/leads/LeadList/Cards/mixins/leadsDueDateMixin'
import CrmTags from '@/modules/leads/LeadList/CrmTags.vue'
import { crmTags } from '@/modules/leads/LeadList/lib/crmTags'
import {
  commissionValue,
  hasPropertyValue,
  propertyValue,
} from '@/modules/leads/LeadList/lib/property'
import nameMixin from '@/modules/leads/mixins/name'
import propertyRequestMixin from '@/modules/leads/mixins/propertyRequest'
import showActionMixin from '@/modules/leads/mixins/showAction'

import { getDefaultHeaders } from '../configs'
import { ReadableInvitationBlockedReason } from '../enums/Invitation'

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100]
const LIST_HEIGHT_SETTINGS = {
  xs: 'calc(100vh - 360px)',
  sm: 'calc(100vh - 390px)',
  md: 'calc(100vh - 390px)',
  lg: 'calc(100vh - 330px)',
  xl: 'calc(100vh - 330px)',
}

export default {
  components: { CrmTags },
  mixins: [
    currencyMixin,
    brandingMixin,
    propertyRequestMixin,
    showActionMixin,
    nameMixin,
    crmMixin,
    leadsDueDateMixin,
  ],
  emits: ['update:selected', 'allVisibleItemsSelected', 'allItemsSelected', 'updateListOptions'],

  props: {
    commission: {
      type: String,
      default: '',
    },
    leads: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showSelect: {
      type: Boolean,
      default: false,
    },
    disableFilters: {
      type: Boolean,
      default: false,
    },
    serverItemsLength: {
      type: Number,
      default: 0,
    },
    selectedLeadIds: {
      type: Array,
      default: () => [],
    },
    listOptions: {
      type: Object,
      required: true,
    },
    columnSettings: {
      type: Array,
      required: true,
    },
    selectItems: {
      type: Function,
      default: () => {},
    },
    activeFiltersCount: {
      type: Number,
      default: 0,
    },
    triggerSelectAllItems: {
      type: Boolean,
      default: false,
    },
    triggerDeselectAllItems: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedValue: [],
      internalOptions: { ...this.listOptions.options },
      internalItemsPerPage: this.listOptions.itemsPerPage,
      isHandlingRowCheckbox: false,
    }
  },

  computed: {
    listHeight() {
      return LIST_HEIGHT_SETTINGS[this.$vuetify.display.name]
    },
    headers() {
      const translatedHeader = getDefaultHeaders().map((col) => ({
        ...col,
        title: this.$te(col.text) ? this.$t(col.text) : col.text,
      }))

      return [
        ...this.columnSettings,
        // Actions must be visible regardless of settings
        { value: 'actions', visible: true },
      ]
        .filter(({ visible }) => visible)
        .filter(({ hideDisplay }) => !hideDisplay)
        .map(({ value }) => translatedHeader.find((col) => col.value === value))
    },

    noDataText() {
      return this.activeFiltersCount > 0
        ? this.$t('leads.table.no-data-texts.0')
        : this.$t('leads.table.no-data-texts.1')
    },

    conversionTypes() {
      return Object.values(this.CONVERSION_TYPES).filter(({ type }) =>
        this.isColumnVisible(`conversionCountByType.${type}`)
      )
    },
  },

  watch: {
    internalOptions() {
      this.$emit('updateListOptions', this.internalOptions)
    },
    selectedLeadIds: {
      handler() {
        this.syncSelectedValueWithLeadIds()
      },
      immediate: true,
    },
    leads() {
      this.syncSelectedValueWithLeadIds()
      // Skip if the page intersection dropped ids (e.g. a cross-page "select all matching"
      // selection) — pushing selectedValue back here would truncate it to this page.
      const selectedLeadIdsCount = Array.isArray(this.selectedLeadIds)
        ? this.selectedLeadIds.length
        : 0
      if (this.showSelect && this.selectedValue.length === selectedLeadIdsCount) {
        this.selectItems(this.selectedValue)
      }
    },
    triggerSelectAllItems() {
      if (this.triggerSelectAllItems) {
        this.selectAllItems()
      }
    },
    triggerDeselectAllItems() {
      if (this.triggerDeselectAllItems) {
        this.deselectAllItems()
      }
    },
  },
  created() {
    this.leadStatus = status
    this.itemsPerPageOptions = ITEMS_PER_PAGE_OPTIONS
    this.CONVERSION_TYPES = {
      VALUATION: {
        type: 'VALUATION',
        label: 'leads.table.actions.valuation',
        color: 'blue-lighten-4',
      },
      PLOT_VALUATION: {
        type: 'PLOT_VALUATION',
        label: 'leads.table.actions.plot-valuation',
        color: 'purple-lighten-4',
      },
      RETURN_CALL: {
        type: 'RETURN_CALL',
        label: 'leads.table.actions.return-call',
        color: 'teal-lighten-4',
      },
      LIFE_ANNUITY: {
        type: 'LIFE_ANNUITY',
        label: 'leads.table.actions.life-annuity',
        color: 'cyan-lighten-4',
      },
      PARTIAL_SALE: {
        type: 'PARTIAL_SALE',
        label: 'leads.table.actions.partial-sale',
        color: 'lime-lighten-4',
      },
      PROPERTY_REQUEST: {
        type: 'PROPERTY_REQUEST',
        label: 'leads.table.actions.property-request',
        color: 'brown-lighten-4',
      },
    }
    this.DOWNLOAD_TYPES = {
      GUIDE: {
        label: 'leads.table.actions.download.guide',
        color: 'deep-purple-lighten-4',
      },
      CHECKLIST: {
        label: 'leads.table.actions.download.checklist',
        color: 'blue-grey-lighten-4',
      },
    }
  },

  methods: {
    hasPropertyValue,
    commissionValue,
    crmTags,
    propertyValue,
    readableInvitationBlockedReason(item) {
      return ReadableInvitationBlockedReason[item.invitationBlockedReason]
    },
    syncSelectedValueWithLeadIds() {
      if (!Array.isArray(this.selectedLeadIds) || this.selectedLeadIds.length === 0) {
        this.selectedValue = []
        return
      }

      this.selectedValue = this.leads.filter(({ id }) => this.selectedLeadIds.includes(id))
    },
    getValuationCount(item) {
      return item?.conversionCountByType?.VALUATION + item?.conversionCountByType?.IN_APP_VALUATION
    },
    showPostalShippingAction(item) {
      return Boolean(
        item.conversions &&
        item.conversions.find(
          (current) =>
            current.download?.postalShipping && current.download.postalShipping !== 'NONE'
        )
      )
    },
    showQuizPreConversionAction(item) {
      return Boolean(item.conversions?.some(({ preConversion }) => preConversion?.type === 'QUIZ'))
    },

    getDownloadConversions(item) {
      const downloads = item.conversions?.filter(({ type }) => type === 'DOWNLOAD')
      const downloadsByType = downloads.reduce((acc, current) => {
        if (!current.landingpage?.type) return acc
        if (acc[current.landingpage?.type]) {
          acc[current.landingpage.type]++
        } else {
          acc[current.landingpage.type] = 1
        }
        return acc
      }, {})

      return downloadsByType
    },

    formatCity(item) {
      const valuationConversion = item.conversions.find(
        (conversion) => conversion.type === 'VALUATION'
      )
      if (valuationConversion) {
        return valuationConversion.valuation.inputs.address.city
      }
      return ''
    },
    formatZip(item) {
      const filterZipCode = this.listOptions?.filters?.zipcode

      const isSpecificZipCode =
        filterZipCode && filterZipCode !== 'ALL_WITH_ZIPCODE' && filterZipCode !== 'NO_ZIPCODE'

      if (isSpecificZipCode) return filterZipCode

      const valuationConversion = item.conversions.find((conversion) => {
        return conversion.type === 'VALUATION'
      })
      if (valuationConversion) {
        return valuationConversion.valuation.inputs.address.zip
      }
      return ''
    },
    getRowClass({ item }) {
      if (!item) {
        return ''
      }
      return {
        class: {
          'lead-list__item': true,
          'lead-list__item--new': item.currentStatus === 'new',
          'lead-list__item-mobile--new':
            this.$vuetify.display.smAndDown && item.currentStatus === 'new',
        },
      }
    },
    handleSelectionUpdate(items) {
      this.selectedValue = Array.isArray(items) ? items : [items]
      this.selectItems(this.selectedValue) // Call the prop function
      this.$emit('update:selected', this.selectedValue)
    },
    handleItemSelection(item, selected) {
      this.isHandlingRowCheckbox = true
      if (selected) {
        this.selectedValue = this.selectedValue.some(({ id }) => id === item.id)
          ? this.selectedValue
          : [...this.selectedValue, item]
      } else {
        this.selectedValue = this.selectedValue.filter(({ id }) => id !== item.id)
      }
      this.selectItems(this.selectedValue)
      setTimeout(() => {
        this.isHandlingRowCheckbox = false
      }, 0)
    },
    isColumnVisible(key) {
      return this.columnSettings.find((setting) => setting.value === key)?.visible
    },
    selectAllVisibleItems(event) {
      if (this.isHandlingRowCheckbox) {
        return
      }
      const isChecked = event?.target?.checked
      const selectedItems = isChecked ? [...this.leads] : []
      this.handleSelectionUpdate(selectedItems)
      this.$emit('allVisibleItemsSelected', {
        items: this.leads,
        checked: isChecked,
      })
    },
    selectAllItems() {
      this.handleSelectionUpdate(this.leads)
      this.$emit('allItemsSelected', true)
    },
    deselectAllItems() {
      this.handleSelectionUpdate([])
      this.$emit('allItemsSelected', false)
    },

    /**
     * We simulate normal link behavior when using cmd/ctrl + click
     * to open the details in a new tab.
     */
    handleRowClick(event, lead) {
      if (event.target.tagName.toLowerCase() === 'input') {
        return
      }
      const { item } = lead
      const href = this.$router.resolve({
        name: 'lead-details',
        params: { id: item.id },
      }).href

      if (event.metaKey || event.ctrlKey) {
        window.open(href, '_blank')
      } else {
        this.$router.push(href)
      }
    },

    /**
     * When clicking on the link we prevent the default row click behavior.
     */
    handleNameClick(event) {
      if (event.metaKey || event.ctrlKey) {
        event.stopPropagation()
      } else {
        event.preventDefault()
      }
    },
  },
}
</script>

<style scoped>
.text-black {
  color: black;
}
.action-row {
  width: 800px;
}

.property-value-icons {
  font-size: 18px;
}

.due-date-red {
  color: #e41d1d;
}

.deactivated-checkbox {
  height: 40px;
  width: 40px;
  align-items: center;
  display: flex;
  justify-content: center;
}

:deep(.bg-red-lighten-4) {
  background-color: #ff9fa7 !important;
}

:deep(.lead-list__item--new > td:first-child),
:deep(.lead-list__item-mobile--new) {
  border-left: 3px solid #fa0143;
  padding: 0 8px 0 5px !important;
}

@media (max-width: 600px) {
  :deep(.v-data-table .v-table__wrapper > table tbody > tr.v-data-table__tr--mobile > td) {
    min-height: 30px;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-pack: justify;
    -ms-flex-pack: justify;
    justify-content: space-between;
  }
  .action-row {
    width: 200px;
  }
}

:deep(.td-small td) {
  max-width: 200px;
  min-width: 100px;
  word-break: break-word;
  white-space: normal;
  text-align: left;
}
</style>
